import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

function euro(v) { return Number(v || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }); }
function isoToDE(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}
function addDays(isoDate, days) {
  const d = new Date(isoDate + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
function todayISO() { return new Date().toISOString().slice(0, 10); }
function endOfMonth(isoDate) {
  const [y, m] = isoDate.split('-');
  return new Date(Date.UTC(Number(y), Number(m), 0)).toISOString().slice(0, 10);
}
function fifteenthOfMonth(isoDate) {
  const [y, m] = isoDate.split('-');
  const fifteenth = `${y}-${m}-15`;
  const today = todayISO();
  return fifteenth >= today ? fifteenth : endOfMonth(isoDate);
}

const CONF_COLOR = { HIGH: '#10b981', MEDIUM: '#f59e0b', LOW: '#94a3b8' };
const CONF_BG    = { HIGH: '#dcfce7', MEDIUM: '#fef9c3', LOW: '#f1f5f9' };
const TYP_LABEL  = { fixkosten: 'Fix', einnahme: 'Einnahme', variabel: 'Variabel', investition: 'Invest' };
const TYP_COLOR  = { fixkosten: '#ef4444', einnahme: '#10b981', variabel: '#f59e0b', investition: '#7c3aed' };
const INTV_LABEL = {
  monthly: 'Monatlich', quarterly: 'Vierteljährlich', halfyear: 'Halbjährlich',
  yearly: 'Jährlich', weekly: 'Wöchentlich', biweekly: 'Zweiwöchentlich'
};

function Badge({ label, bg, color }) {
  return (
    <span style={{ background: bg, color, fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

const card = { background: 'var(--card-bg,#fff)', border: '1px solid var(--border,#e2e8f0)', borderRadius: 10, padding: '12px 14px', boxShadow: '0 1px 4px rgba(15,23,42,.05)' };
const muted = { color: '#64748b', fontSize: 12 };

export default function ZahlungsvorschauPage() {
  const nav = useNavigate();
  const today = todayISO();

  const [from, setFrom] = useState(today);
  const [to, setTo]     = useState(addDays(today, 30));
  const [includeInvestments,   setIncludeInvestments]   = useState(false);
  const [includeLowConfidence, setIncludeLowConfidence] = useState(false);
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get('/forecast/payments', {
        params: { from, to, include_investments: includeInvestments, include_low_confidence: includeLowConfidence }
      });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Fehler beim Laden');
      setData(null);
    } finally { setLoading(false); }
  }, [from, to, includeInvestments, includeLowConfidence]);

  useEffect(() => { load(); }, [load]);

  function applyQuick(key) {
    const t = todayISO();
    if (key === 'today')   { setFrom(t); setTo(t); }
    if (key === '7days')   { setFrom(t); setTo(addDays(t, 7)); }
    if (key === 'bis15')   { setFrom(t); setTo(fifteenthOfMonth(t)); }
    if (key === 'monat')   { setFrom(t); setTo(endOfMonth(t)); }
    if (key === '30days')  { setFrom(t); setTo(addDays(t, 30)); }
    if (key === '90days')  { setFrom(t); setTo(addDays(t, 90)); }
  }

  const items   = data?.items   || [];
  const totals  = data?.totals  || {};
  const pending = items.filter(i => !i.alreadyBooked);
  const booked  = items.filter(i => i.alreadyBooked);

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 0 40px' }}>
      <h1 style={{ margin: '0 0 4px', color: '#0f172a', fontSize: 22 }}>Zahlungsvorschau</h1>
      <p style={{ ...muted, margin: '0 0 16px' }}>Geplante Abbuchungen im gewählten Zeitraum, basierend auf bestätigten wiederkehrenden Serien.</p>

      {/* ── Filter-Panel ── */}
      <div style={{ ...card, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          {[
            { k: 'today',  l: 'Heute' },
            { k: '7days',  l: 'Nächste 7 Tage' },
            { k: 'bis15',  l: 'Bis 15.' },
            { k: 'monat',  l: 'Monatsende' },
            { k: '30days', l: '30 Tage' },
            { k: '90days', l: '90 Tage' },
          ].map(q => (
            <button key={q.k} onClick={() => applyQuick(q.k)} style={{
              padding: '5px 11px', borderRadius: 6, border: '1px solid #cbd5e1',
              background: '#f8fafc', color: '#334155', fontSize: 13, cursor: 'pointer'
            }}>{q.l}</button>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 13, color: '#475569' }}>
            Von
            <input type="date" value={from} onChange={e => setFrom(e.target.value)}
              style={{ padding: '7px 9px', border: '1px solid #cbd5e1', borderRadius: 7, fontSize: 14 }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 13, color: '#475569' }}>
            Bis
            <input type="date" value={to} onChange={e => setTo(e.target.value)}
              style={{ padding: '7px 9px', border: '1px solid #cbd5e1', borderRadius: 7, fontSize: 14 }} />
          </label>
        </div>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={includeLowConfidence} onChange={e => setIncludeLowConfidence(e.target.checked)} />
            Unsichere Serien (LOW) einbeziehen
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={includeInvestments} onChange={e => setIncludeInvestments(e.target.checked)} />
            Investitionen einbeziehen
          </label>
        </div>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 9, padding: '10px 14px', marginBottom: 12, fontWeight: 700 }}>
          {error}
        </div>
      )}

      {/* ── Summen-Kacheln ── */}
      {!loading && data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 12 }}>
          <SummaryCard title="Erwartet" value={euro(totals.erwartet)} hint={`${totals.anzahlPending ?? 0} offene Posten`} color="#ef4444" />
          <SummaryCard title="Davon variabel" value={euro(totals.variabel)} hint="Betragstoleranz > 15 %" color="#f59e0b" />
          <SummaryCard title="Unsicher (LOW)" value={euro(totals.unsicher)} hint={`${includeLowConfidence ? 'einbezogen' : 'ausgeblendet'}`} color="#94a3b8" />
        </div>
      )}

      {loading && <div style={{ ...card, color: '#64748b' }}>Wird geladen...</div>}

      {/* ── Zahlungsliste ── */}
      {!loading && data && pending.length === 0 && booked.length === 0 && (
        <div style={{ ...card, color: '#64748b', textAlign: 'center', padding: 28 }}>
          Keine geplanten Zahlungen im gewählten Zeitraum.
        </div>
      )}

      {!loading && pending.length > 0 && (
        <>
          <h2 style={{ margin: '0 0 8px', fontSize: 15, color: '#0f172a' }}>Ausstehend ({pending.length})</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 16 }}>
            {pending.map((item, i) => <PaymentRow key={i} item={item} nav={nav} />)}
          </div>
        </>
      )}

      {!loading && booked.length > 0 && (
        <>
          <h2 style={{ margin: '0 0 8px', fontSize: 15, color: '#64748b' }}>Bereits gebucht ({booked.length})</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, opacity: 0.6 }}>
            {booked.map((item, i) => <PaymentRow key={i} item={item} nav={nav} booked />)}
          </div>
        </>
      )}

      <div style={{ marginTop: 20, textAlign: 'center' }}>
        <button onClick={() => nav('/wiederkehrend')}
          style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#334155', fontSize: 13, cursor: 'pointer' }}>
          Serien verwalten
        </button>
      </div>
    </div>
  );
}

function SummaryCard({ title, value, hint, color }) {
  return (
    <div style={{ background: 'var(--card-bg,#fff)', border: `1px solid ${color}44`, borderRadius: 10, padding: '10px 13px', boxShadow: '0 1px 3px rgba(15,23,42,.04)' }}>
      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{hint}</div>
    </div>
  );
}

function PaymentRow({ item, nav, booked }) {
  const betrag = item.erwarteter_betrag || 0;
  const isNeg  = betrag < 0 || (item.typ !== 'einnahme' && betrag === 0);
  return (
    <div style={{
      background: 'var(--card-bg,#fff)', border: '1px solid var(--border,#e2e8f0)',
      borderRadius: 9, padding: '10px 14px',
      display: 'grid', gridTemplateColumns: '52px 1fr auto',
      gap: '0 10px', alignItems: 'center'
    }}>
      {/* Datum */}
      <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', lineHeight: 1.2, textAlign: 'center' }}>
        <div>{isoToDE(item.datum).slice(0, 5)}</div>
        <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400 }}>{isoToDE(item.datum).slice(6)}</div>
      </div>

      {/* Bezeichnung + Meta */}
      <div>
        <div style={{ fontWeight: 600, fontSize: 14, color: booked ? '#94a3b8' : '#1e293b', display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
          {item.bezeichnung || item.empfaenger}
          {booked && <Badge label="gebucht" bg="#f1f5f9" color="#64748b" />}
          {item.isVariable && !booked && <Badge label="variabel" bg="#fef9c3" color="#a16207" />}
        </div>
        <div style={{ ...muted, marginTop: 2, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {item.empfaenger && item.empfaenger !== item.bezeichnung && (
            <span>{item.empfaenger}</span>
          )}
          {item.kategorie && <span>· {item.kategorie}</span>}
          <span>· {INTV_LABEL[item.intervall] || item.intervall}</span>
        </div>
      </div>

      {/* Betrag + Badges */}
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: isNeg ? '#ef4444' : '#10b981', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {euro(betrag)}
        </div>
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', marginTop: 3, flexWrap: 'wrap' }}>
          <Badge label={item.confidence || 'MEDIUM'}
            bg={CONF_BG[item.confidence] || CONF_BG.MEDIUM}
            color={CONF_COLOR[item.confidence] || CONF_COLOR.MEDIUM} />
          {item.typ && (
            <Badge label={TYP_LABEL[item.typ] || item.typ}
              bg={(TYP_COLOR[item.typ] || '#64748b') + '22'}
              color={TYP_COLOR[item.typ] || '#64748b'} />
          )}
        </div>
      </div>
    </div>
  );
}
